import { ComponentProps } from 'react';

import { ChevronDown16 } from '@ingrid/iconography/react';
import { DropdownMenu, LoadingButton, Tooltip } from '@ingrid/luterkort-react';

import { ConditionalWrapper } from '~src/components/ConditionalWrapper/ConditionalWrapper';
import { useTranslation } from '~src/i18n/use-translation';
import { changeFileExtension, extractFileName } from '~src/utils/file';

import { useShipmentActions } from '../hooks';
import { useGenerateLabelsUrlMutation } from '../mutations';
import { ShipmentDetails } from '../types';

interface Props {
  shipment: Pick<ShipmentDetails, 'direction_type' | 'status'>;
  size?: ComponentProps<typeof LoadingButton>['size'];
  labelUrl: string | undefined;
}

export function DownloadLabelButton(props: Props) {
  const { labelUrl, shipment, size } = props;
  const { t } = useTranslation();

  const actions = useShipmentActions({
    shipment: { direction_type: shipment.direction_type, status: shipment.status.type },
  });

  const mutation = useGenerateLabelsUrlMutation();
  const isDisabled = !actions.includes('PRINT_LABEL');

  async function handleDownload(ext: string, url: string) {
    const { data } = await mutation.mutateAsync({ file_name: changeFileExtension(extractFileName(url), ext) });
    window.open(data.url);
  }

  // Don't show the button if there's no label
  if (!labelUrl) {
    return null;
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        {/* This `div` is needed because DropdownMenu.Trigger automatically appends refs & handlers to it's child. */}
        {/* Which means without the `div` those handlers ends up on the Tooltip, or it's Fragment, which we don't want. */}
        <div>
          <ConditionalWrapper
            when={isDisabled}
            wrapper={c => (
              <Tooltip content={t('shipment.details.notPrintable')}>
                <span>{c}</span>
              </Tooltip>
            )}
          >
            <LoadingButton loading={mutation.isPending} variant="secondary" disabled={isDisabled} size={size}>
              {t('common.downloadLabel')}
              <ChevronDown16 />
            </LoadingButton>
          </ConditionalWrapper>
        </div>
      </DropdownMenu.Trigger>

      <DropdownMenu.Content align="start">
        {['.pdf', '.png', '.svg', '.zpl'].map(ext => (
          <DropdownMenu.Item key={ext} onClick={() => handleDownload(ext, labelUrl)}>
            {ext}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}
